/**
 * Did this failure mean the member's Auth0 session is gone?
 *
 * `getAccessTokenSilently` rejects for two very different reasons, and the apps
 * have to tell them apart. A fetch that failed because the network dropped is
 * worth retrying and worth reporting. A session that ended is neither: the only
 * thing to do is send the member to log in, and reporting it fills Sentry with
 * crashes for something that is supposed to happen.
 *
 * The shapes come from auth0-spa-js (bundled inside @auth0/auth0-react):
 *
 *  - `GenericError` carries an OAuth code on `error`. `login_required` is what a
 *    silent-auth iframe returns when the Auth0 session cookie is not readable —
 *    the everyday case on iOS Safari, which blocks it as third-party.
 *  - `MissingRefreshTokenError` carries no `error` field at all, only a message,
 *    so the codes alone are not enough.
 */
const SESSION_ENDED_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
])

// Matched case-insensitively against `message`. Deliberately short: these are
// substrings of what auth0-spa-js throws, not whole messages.
const SESSION_ENDED_MESSAGES = ['missing refresh token', 'login required', 'invalid refresh token']

export function isSessionExpiredError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false

  const code = (e as { error?: unknown }).error
  if (typeof code === 'string' && SESSION_ENDED_CODES.has(code)) return true

  const message = (e as { message?: unknown }).message
  if (typeof message !== 'string') return false
  const haystack = message.toLowerCase()
  return SESSION_ENDED_MESSAGES.some((needle) => haystack.includes(needle))
}

/**
 * The reportable shape of a rejected auth call.
 *
 * Stringifying an OAuthError loses the one field worth having — the OAuth code
 * on `error` — so pull the three diagnostic fields out by hand. Nothing here
 * carries a token or an email: `error_description` is Auth0's own copy, written
 * to be shown to the user.
 */
export function describeAuthError(e: unknown): Record<string, string> {
  if (!e || typeof e !== 'object') return { error: String(e) }

  const out: Record<string, string> = {}
  const {
    error,
    error_description: description,
    message,
  } = e as {
    error?: unknown
    error_description?: unknown
    message?: unknown
  }
  if (typeof error === 'string') out.error = error
  if (typeof description === 'string') out.error_description = description
  if (typeof message === 'string') out.message = message
  return out
}
